/**
 * Pointer-up behaviour: commit what the ghost was previewing.
 *
 * Right-click is the universal shortcut — a free beam end gets a standard, a
 * stair cycles its handrail side, anything else is deleted.
 */

import { ALP_LENGTHS, bracketName, KR, nodeY } from "../../data/haki.ts";
import { bayFrame, findBeam, kXZ, nearestEdgeInfo, nearestOf, orientCorners } from "../../model/geometry.ts";
import {
  addBeam,
  addBoard,
  addBracket,
  addLeg,
  boardLength,
  ensureGuardrailPosts,
  findRail,
  findToe,
  inwardFor,
  removeBracket,
  removeStandard,
} from "../../model/operations.ts";
import { autoSpir, legHeight, snapXZ } from "../../model/solver.ts";
import { isMounted, type Point, type ScaffoldModel } from "../../model/types.ts";
import type { PartRef } from "../partRef.ts";
import { aimEnd } from "./aim.ts";
import {
  BEAM_KIND_LABEL,
  beamKindNote,
  describePart,
  nextRailSide,
  otherBeamKind,
  railSideLabel,
  railToggleable,
  rightClickAction,
} from "./describe.ts";
import {
  chooseBoardSpan,
  outwardFromEdge,
  planBoard,
  planFreeEnd,
  resolveUtvPlacement,
  stackTarget,
} from "./shared.ts";
import type { ToolContext } from "./toolContext.ts";

/** The part lists a plain splice removes from; decks also answer for their planks. */
function indexedList(model: ScaffoldModel, type: PartRef["type"]): unknown[] | null {
  switch (type) {
    case "beam":
      return model.beams;
    case "diag":
      return model.diags;
    case "stair":
      return model.stairs;
    case "ladder":
      return model.ladders;
    case "rail":
      return model.rails;
    case "toe":
      return model.toes;
    case "board":
      return model.boards;
    case "deck":
    case "plank":
      return model.decks;
    default:
      return null;
  }
}

export function deletePart(ctx: ToolContext, ref: PartRef): void {
  // Described before the splice, while the index still names this part.
  const description = describePart(ctx.model, ref);
  ctx.snapshot();

  if (ref.type === "leg") {
    const leg = ctx.model.legs.get(ref.key);
    // Take only the piece stacked on top; the standard under it stays. On a
    // bracket every segment is such a piece, down to the bare stub.
    if (leg && ref.top && leg.segments.length > (isMounted(leg) ? 0 : 1)) {
      leg.segments.pop();
    } else if (leg) {
      removeStandard(ctx.model, leg);
      ctx.selectLeg(null);
    }
  } else if (ref.type === "bracket") {
    const bracket = ctx.model.brackets[ref.idx];
    if (bracket) {
      removeBracket(ctx.model, bracket);
    }
  } else if ("idx" in ref) {
    indexedList(ctx.model, ref.type)?.splice(ref.idx, 1);
  }

  ctx.clearHot();
  ctx.rebuild();
  ctx.hud(`Deleted: ${description}`);
}

function togglePlank(ctx: ToolContext, idx: number, slot: number): void {
  const deck = ctx.model.decks[idx];
  if (!deck) {
    return;
  }

  ctx.snapshot();
  if (deck.removed.has(slot)) {
    deck.removed.delete(slot);
  } else {
    deck.removed.add(slot);
  }
  ctx.rebuild();
}

function placeLeg(
  ctx: ToolContext,
  x: number,
  z: number,
  segs: number[],
  exact: boolean,
  gyHint?: number,
): void {
  const q = exact ? { x: +x.toFixed(3), z: +z.toFixed(3) } : snapXZ(ctx.model, x, z, ctx.settings.snap);
  if (ctx.model.legs.has(kXZ(q.x, q.z))) {
    ctx.hud("There is already a standard here");
    return;
  }

  const gy = ctx.terrain.resolveGy(q.x, q.z, gyHint);
  addLeg(ctx.model, q.x, q.z, segs, gy);
  ctx.rebuild();

  const surface = gy > 0.05 ? ` (surface ${gy.toFixed(2)} m)` : "";
  ctx.hud(`Standard placed at ${q.x.toFixed(2)}, ${q.z.toFixed(2)}${surface}`);
}

function clickStandard(ctx: ToolContext, event: PointerEvent): void {
  const ghost = ctx.pickGhost(event);
  if (ghost && ghost.payload.kind === "legAt") {
    const { x, z, p } = ghost.payload;
    const plan = planFreeEnd(ctx, x, z, p);
    ctx.snapshot();
    placeLeg(ctx, x, z, plan.segs, true, plan.gy);
    return;
  }

  const target = stackTarget(ctx, event);
  if (target) {
    const onSpigot = isMounted(target) && target.segments.length === 0;
    ctx.snapshot();
    target.segments.push(ctx.settings.size);
    ctx.rebuild();
    ctx.selectLeg(kXZ(target.x, target.z));
    const total = legHeight(target).toLocaleString("en", { minimumFractionDigits: 1 });
    const where = onSpigot ? "set on the bracket's spigot" : "stacked on top";
    ctx.hud(`Standard S4 AL ${ctx.settings.size} ${where} — ${total} m total`);
    return;
  }

  const surface = ctx.pickCollider(event);
  if (surface) {
    const point = ctx.terrain.surfacePoint(surface);
    ctx.snapshot();
    placeLeg(ctx, point.x, point.z, [ctx.settings.size], false, point.gy);
  }
}

function clickBeam(ctx: ToolContext, event: PointerEvent): void {
  if (!ctx.pending) {
    const node = ctx.pickNode(event);
    if (node) {
      ctx.pending = { kind: "beam", leg: node.leg, p: node.p };
      ctx.refreshGhosts();
      ctx.hud("Aim and click — Esc cancels", true);
      return;
    }

    const hit = ctx.pickElem(event, (ref) => ref.type === "beam");
    if (hit && hit.ref.type === "beam") {
      ctx.snapshot();
      ctx.model.beams.splice(hit.ref.idx, 1);
      ctx.rebuild();
      ctx.hud("Beam removed");
    }
    return;
  }

  const pending = ctx.pending;
  const y = nodeY(pending.p) + KR;
  const plane = ctx.planeAt(event, y);
  if (!plane) {
    return;
  }

  const result = aimEnd({
    model: ctx.model,
    from: pending.leg,
    y,
    planePoint: plane,
    hoveredLeg: ctx.hoveredLeg(event),
    selectedSize: ctx.settings.size,
  });

  if (!result.fits) {
    ctx.hud("The distance fits no beam module (700–3050)");
    return;
  }
  if (findBeam(ctx.model, pending.leg, result.end, pending.p)) {
    ctx.hud("There is already a beam here");
    return;
  }

  const end = result.target ? result.end : snapXZ(ctx.model, result.end.x, result.end.z, ctx.settings.snap);
  ctx.snapshot();
  addBeam(ctx.model, pending.leg, end, pending.p, ctx.settings.beamKind, result.len);

  let warning = "";
  if (!result.target && ctx.settings.autoSpir) {
    const gy = ctx.terrain.resolveGy(end.x, end.z, nodeY(pending.p));
    const plan = autoSpir(pending.p, gy, pending.leg, (x, z) => ctx.terrain.groundY(x, z));
    addLeg(ctx.model, end.x, end.z, plan.segs, gy);
    if (!plan.reaches) {
      warning = " — the auto-standard does not reach, stack a segment";
    }
  }

  ctx.pending = null;
  ctx.rebuild();

  const kind = ctx.settings.beamKind === "lb" ? "LB " : "ERB ";
  const adjusted = result.adjusted ? ` (adjusted from ${ctx.settings.size})` : "";
  const tail =
    result.target || ctx.settings.autoSpir
      ? `placed${warning}`
      : "placed with a free end — right-click the end to place a standard";
  ctx.hud(`${kind}${result.len}${adjusted} ${tail}`);
}

function clickDiagonal(ctx: ToolContext, event: PointerEvent): void {
  const node = ctx.pickNode(event);
  if (node) {
    if (!ctx.pending) {
      ctx.pending = { kind: "diag", leg: node.leg, p: node.p };
      ctx.refreshGhosts();
      ctx.hud("Click the upper cup on another standard", true);
      return;
    }
    if (node.leg === ctx.pending.leg) {
      ctx.hud("Pick a cup on another standard");
      return;
    }

    ctx.snapshot();
    const pending = ctx.pending;
    const lower = node.p < pending.p ? node : pending;
    const upper = node.p < pending.p ? pending : node;
    ctx.model.diags.push({
      a: { x: lower.leg.x, z: lower.leg.z },
      pa: lower.p,
      b: { x: upper.leg.x, z: upper.leg.z },
      pb: upper.p,
    });
    ctx.pending = null;
    ctx.rebuild();
    return;
  }

  const hit = ctx.pickElem(event, (ref) => ref.type === "diag");
  if (hit && hit.ref.type === "diag") {
    ctx.snapshot();
    ctx.model.diags.splice(hit.ref.idx, 1);
    ctx.rebuild();
    ctx.hud("Diagonal removed");
  }
}

/** Lay one board in the slot under the pointer, or take a laid board away. */
function clickBoard(ctx: ToolContext, event: PointerEvent): void {
  const ghost = ctx.pickGhost(event);
  if (ghost && ghost.payload.kind === "board") {
    const { wid } = ghost.payload;
    const span = chooseBoardSpan(ctx.model, wid, ghost.payload.span.p, ghost.point) ?? ghost.payload.span;
    const place = ghost.payload.place ?? planBoard(ctx.model, span, wid, ghost.point);
    if (place.blocked) {
      ctx.hud("This slot is already covered");
      return;
    }

    ctx.snapshot();
    addBoard(ctx.model, place.board);
    ctx.rebuild();
    ctx.hud(`AL plank ALP ${wid}×${boardLength(place.board)} laid — next slot is ready`);
    return;
  }

  const hit = ctx.pickElem(event, (ref) => ref.type === "board");
  if (hit) {
    deletePart(ctx, hit.ref);
  }
}

function clickDeck(ctx: ToolContext, event: PointerEvent): void {
  if (ctx.settings.deckKind === "board") {
    clickBoard(ctx, event);
    return;
  }

  const ghost = ctx.pickGhost(event);
  if (ghost && ghost.payload.kind === "deck") {
    ctx.snapshot();
    // As in the hover: planks span from the nearest edge to the one opposite.
    const info = nearestEdgeInfo(ghost.payload.bay.c, ghost.point);
    const corners = orientCorners(ghost.payload.bay.c, !info.alongAC);
    const frame = bayFrame(corners);
    const len = nearestOf(ALP_LENGTHS, frame.L * 1000);

    ctx.model.decks.push({
      c: corners,
      p: ghost.payload.bay.p,
      hatch: false,
      removed: new Set(),
      len,
      wid: Math.round(frame.W * 1000),
      fam: ctx.settings.plankFam,
    });
    ctx.rebuild();
    ctx.hud(`Bay decked — ALP ${len} along the chosen beam`);
    return;
  }

  const plank = ctx.pickPlank(event);
  if (plank) {
    togglePlank(ctx, plank.idx, plank.slot);
  }
}

function clickRail(ctx: ToolContext, event: PointerEvent): void {
  const ghost = ctx.pickGhost(event);
  if (ghost && ghost.payload.kind === "rail") {
    const beam = ghost.payload.beam;
    const a: Point = { x: beam.a.x, z: beam.a.z };
    const b: Point = { x: beam.b.x, z: beam.b.z };
    ctx.snapshot();

    if (ctx.settings.railKind !== "toe" && !findRail(ctx.model, a, b, beam.p)) {
      ctx.model.rails.push({ a, b, p: beam.p });
    }
    if (ctx.settings.railKind !== "skrd" && !findToe(ctx.model, a, b, beam.p)) {
      ctx.model.toes.push({ a, b, p: beam.p, ...inwardFor(ctx.model, a, b, beam.p, ghost.point) });
    }

    // The frame hooks the standard at both ends, so they must reach its top.
    const extended = ensureGuardrailPosts(ctx.model, (x, z) => ctx.terrain.groundY(x, z));
    ctx.rebuild();
    ctx.hud(extended > 0 ? `Added · ${extended} standards extended to reach the top connector` : "Added");
    return;
  }

  const hit = ctx.pickElem(event, (ref) => ref.type === "rail" || ref.type === "toe");
  if (hit) {
    deletePart(ctx, hit.ref);
  }
}

/**
 * Place a bracket where its ghost shows. An SKD is a diagonal only, so its arm
 * — the 1250 beam it is combined with — goes in with it, in the selected kind.
 */
function clickBracket(ctx: ToolContext, event: PointerEvent): void {
  const ghost = ctx.pickGhost(event);
  if (ghost && ghost.payload.kind === "bracket") {
    const { bracketKind, a, b, p, len } = ghost.payload;
    ctx.snapshot();
    addBracket(ctx.model, { kind: bracketKind, a, b, p, len });
    if (bracketKind === "skd") {
      addBeam(ctx.model, a, b, p, ctx.settings.beamKind, len);
    }
    ctx.rebuild();

    const arm = bracketKind === "skd" ? ` + ${BEAM_KIND_LABEL[ctx.settings.beamKind]} ${len}` : "";
    ctx.hud(
      `${bracketName(bracketKind, len)}${arm} placed — beam between the brackets, deck, then guardrail`,
    );
    return;
  }

  const hit = ctx.pickElem(event, (ref) => ref.type === "bracket");
  if (hit) {
    deletePart(ctx, hit.ref);
  }
}

/** Select the plane under the pointer, or deselect on empty space. A press that began on the gizmo is its own gesture. */
function clickPlane(ctx: ToolContext, event: PointerEvent): void {
  if (ctx.wasPressOnGizmo()) {
    return;
  }

  const id = ctx.pickPlane(event);
  ctx.selectPlane(id);
  const name = id === null ? null : (ctx.planes.planeById(id)?.name ?? "plane");
  ctx.hud(
    name === null
      ? "No plane selected"
      : `${name} selected — drag the handles, or edit it in the Planes panel`,
  );
}

/** Select the scaffold under the pointer for the gizmo, or deselect on empty space. */
function clickMove(ctx: ToolContext, event: PointerEvent): void {
  if (ctx.wasPressOnGizmo()) {
    return;
  }

  const group = ctx.groupUnder(event);
  ctx.selectScaffold(group);
  ctx.hud(
    group
      ? `Selected — ${group.legKeys.size} standards · drag the arrows to move it`
      : "No scaffold selected",
  );
}

function clickAccess(ctx: ToolContext, event: PointerEvent): void {
  const ghost = ctx.pickGhost(event);
  if (!ghost) {
    const hit = ctx.pickElem(event, (ref) => ref.type === "stair");
    const stair = hit ? railToggleable(ctx.model, hit.ref) : null;
    if (stair) {
      ctx.snapshot();
      stair.rail = nextRailSide(stair.rail);
      ctx.rebuild();
      ctx.hud(`Handrail: ${railSideLabel(stair.rail)}`);
    }
    return;
  }

  if (ghost.payload.kind === "luke") {
    const deck = ctx.model.decks[ghost.payload.deckIdx];
    if (!deck) {
      return;
    }

    ctx.snapshot();
    if (deck.hatch) {
      deck.hatch = false;
      ctx.rebuild();
      ctx.hud("Hatch deck and ladder removed");
      return;
    }

    const info = nearestEdgeInfo(deck.c, ghost.point);
    deck.hatch = true;
    deck.hatchEnd = info.end;
    deck.hatchSide = info.side;
    deck.removed = new Set();
    ctx.rebuild();
    ctx.hud("Hatch deck AL 690 + Ladder ST 2100 added — ladder leans toward the opposite side");
    return;
  }

  if (ghost.payload.kind === "ladder") {
    const { beam } = ghost.payload;
    const out = outwardFromEdge(beam.a, beam.b, ghost.point);
    ctx.snapshot();
    ctx.model.ladders.push({
      a: { x: beam.a.x, z: beam.a.z },
      b: { x: beam.b.x, z: beam.b.z },
      p: beam.p,
      ...out,
    });
    ctx.rebuild();
    ctx.hud("Ladder ST 2100 placed on the outside");
    return;
  }

  if (ghost.payload.kind === "utvs") {
    const { beam, rise } = ghost.payload;
    const out = outwardFromEdge(beam.a, beam.b, ghost.point);
    ctx.snapshot();
    ctx.model.stairs.push({
      small: rise,
      a: { x: beam.a.x, z: beam.a.z },
      b: { x: beam.b.x, z: beam.b.z },
      p: beam.p,
      ...out,
    });
    ctx.rebuild();
    ctx.hud(`UTV stair ${rise} AL placed`);
    return;
  }

  if (ghost.payload.kind === "utv") {
    const placement = ghost.payload.place ?? resolveUtvPlacement(ghost.payload.spans, ghost.point);
    if (!placement) {
      return;
    }

    ctx.snapshot();
    ctx.model.stairs.push({
      utv: placement.len,
      p: placement.p,
      o: { x: +placement.o.x.toFixed(3), z: +placement.o.z.toFixed(3) },
      dx: placement.dx,
      dz: placement.dz,
    });
    ctx.rebuild();
    ctx.hud(`UTV stair ${placement.len}×2000 AL latched between the transoms`);
  }
}

export function handleClick(ctx: ToolContext, event: PointerEvent): void {
  const { tool } = ctx.settings;

  if (tool === "nav") {
    const plank = ctx.pickPlank(event);
    if (plank) {
      togglePlank(ctx, plank.idx, plank.slot);
    }
    return;
  }
  if (tool === "move") {
    clickMove(ctx, event);
    return;
  }
  if (tool === "spir") {
    clickStandard(ctx, event);
    return;
  }
  if (tool === "beam") {
    clickBeam(ctx, event);
    return;
  }
  if (tool === "diag") {
    clickDiagonal(ctx, event);
    return;
  }
  if (tool === "deck") {
    clickDeck(ctx, event);
    return;
  }
  if (tool === "rail") {
    clickRail(ctx, event);
    return;
  }
  if (tool === "bracket") {
    clickBracket(ctx, event);
    return;
  }
  if (tool === "plane") {
    clickPlane(ctx, event);
    return;
  }
  if (tool === "access") {
    clickAccess(ctx, event);
    return;
  }
  if (tool === "del") {
    const hit = ctx.pickElem(event);
    if (hit) {
      deletePart(ctx, hit.ref);
    }
  }
}

export function handleRightClick(ctx: ToolContext, event: PointerEvent): void {
  const end = ctx.pickFreeEnd(event);
  if (end) {
    ctx.clearHot();
    const plan = planFreeEnd(ctx, end.x, end.z, end.p);
    ctx.snapshot();
    placeLeg(ctx, end.x, end.z, plan.segs, true, plan.gy);
    return;
  }

  if (ctx.pending) {
    ctx.clearHot();
    ctx.pending = null;
    ctx.refreshGhosts();
    ctx.hud("Cancelled");
    return;
  }

  const hit = ctx.pickElem(event);
  if (!hit) {
    ctx.clearHot();
    return;
  }

  const action = rightClickAction(ctx.model, hit.ref);

  if (action.kind === "cycleRail") {
    ctx.snapshot();
    action.stair.rail = nextRailSide(action.stair.rail);
    ctx.rebuild();
    ctx.hud(`Handrail: ${railSideLabel(action.stair.rail)}`);
    ctx.clearHot();
    return;
  }

  if (action.kind === "switchBeam") {
    ctx.snapshot();
    action.beam.kind = otherBeamKind(action.beam.kind);
    ctx.rebuild();
    const note = beamKindNote(action.beam.kind, action.beam.len);
    ctx.hud(`Switched to ${BEAM_KIND_LABEL[action.beam.kind]} ${action.beam.len}${note}`);
    ctx.clearHot();
    return;
  }

  deletePart(ctx, hit.ref);
}
